import { Loader2 } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { LoadError } from '@/components/ui/load-state';
import { cn } from '@/lib/utils';

export const errorText = (error: unknown, fallback: string): string => (error instanceof Error && error.message ? error.message : fallback);

/** Loads on mount and when `deps` change; `reload` runs it again. A stale answer never replaces a newer one. */
export function useLoad<T>(load: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const latest = useRef(0);
  const run = useCallback(() => {
    const id = ++latest.current;
    setLoading(true);
    setError(undefined);
    load()
      .then((value) => id === latest.current && setData(value))
      .catch((err: unknown) => id === latest.current && setError(errorText(err, 'Failed to load')))
      .finally(() => id === latest.current && setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `deps` is the caller's list
  }, deps);
  useEffect(run, [run]);
  return { data, setData, error, loading, reload: run };
}

export type Paging = { key: string; tokens: Array<string | undefined>; index: number };

/** The paging for this filter: a new filter key starts at page one with no token, in the same step. */
export function pagingFor(paging: Paging, key: string): Paging {
  return paging.key === key ? paging : { key, tokens: [undefined], index: 0 };
}

/** A list that `ai` pages with tokens: rows of the page shown, and Previous and Next that follow the tokens. */
export function useCursorList<T>(fetchPage: (token?: string) => Promise<{ rows: T[]; next?: string }>, deps: unknown[]) {
  const key = JSON.stringify(deps);
  const [stored, setStored] = useState<Paging>({ key, tokens: [undefined], index: 0 });
  // A filter change resets the token in this render, so one request goes out and it has no old token.
  const paging = pagingFor(stored, key);
  if (paging !== stored) setStored(paging);
  const { data, error, loading, reload } = useLoad(() => fetchPage(paging.tokens[paging.index]), [paging]);
  const next = data?.next;
  const { index } = paging;
  return {
    rows: data?.rows ?? [],
    error,
    loading,
    reload,
    pager: {
      mode: 'cursor' as const,
      pageSize: 50,
      loadedCount: data?.rows.length ?? 0,
      hasPrev: index > 0,
      hasNext: Boolean(next),
      disabled: loading,
      onPrev: () => setStored({ ...paging, index: Math.max(0, index - 1) }),
      onNext: () => {
        if (next) setStored({ ...paging, tokens: [...paging.tokens.slice(0, index + 1), next], index: index + 1 });
      },
    },
  };
}

/** Asks first, runs the write, and says what happened. Returns whether it ran. */
export async function confirmedWrite(question: string, write: () => Promise<unknown>, done: string, failed: string): Promise<boolean> {
  if (!window.confirm(question)) return false;
  try {
    await write();
    toast.success(done);
    return true;
  } catch (error) {
    toast.error(errorText(error, failed));
    return false;
  }
}

/** A section label on the page background, above the card it names. */
export function SectionLabel({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
      <h2 className="text-sm font-semibold text-foreground">{children}</h2>
      {actions}
    </div>
  );
}

export function Loading({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      {label}
    </div>
  );
}

export function LoadFailed({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="rounded-xl border border-border bg-card">
      <LoadError message={message} onRetry={onRetry} />
    </div>
  );
}

/** A small on and off switch. */
export function Toggle({ checked, onChange, label, disabled }: { checked: boolean; onChange: (next: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border border-border transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-foreground' : 'bg-muted',
      )}
    >
      <span className={cn('inline-block h-4 w-4 rounded-full bg-background shadow transition-transform', checked ? 'translate-x-4' : 'translate-x-0.5')} />
    </button>
  );
}

/** Table columns: the title column left, every other column centered, header over contents. */
export const columns = (labels: string[]) => labels.map((label, i) => ({ label, className: i === 0 ? 'text-left whitespace-nowrap' : 'text-center whitespace-nowrap' }));
/** A centered cell on one line. A table wider than its box scrolls inside the box. */
export const CENTER = 'text-center whitespace-nowrap';
/** The first cell of a row, left aligned. */
export const TITLE = 'font-medium whitespace-nowrap';
